import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Menu, MenuItem, Checkbox, FormControlLabel } from "@mui/material";
import JSZip from "jszip";
import axios from "../../configApi/axiosConfig";
import { APIURL } from "../../configApi/apiConfig";
import { useNotificationContext } from "../AuthContext/NotificationContext";
import { FiltrosCarteraBS } from "./FiltrosCarteraBS";
import CarteraTableRow from "./CarteraTableRow";
import SolidarioCheckbox from "./SolidarioCheckbox";

// Primer banco del futuro selector multi-banco de generación de cartera. Los
// demás quedan listados pero deshabilitados hasta que se integren sus propios
// endpoints de generación — no se crea un módulo aparte para esto todavía,
// para no sobre-construir antes de tener un segundo banco real.
const BANCOS = [
  { value: "solidario", label: "Banco Solidario", disabled: false },
  { value: "pichincha", label: "Banco Pichincha (próximamente)", disabled: true },
  { value: "guayaquil", label: "Banco Guayaquil (próximamente)", disabled: true },
];

const hoyISO = () => new Date().toISOString().split("T")[0];
const PLACEHOLDER_ROWS = [{ opacity: "opacity-40" }, { opacity: "opacity-20" }];

const Spinner = ({ className = "h-5 w-5 text-white" }) => (
  <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

export function CarteraBancoSolidario() {
  const { addNotification } = useNotificationContext();

  const [banco, setBanco] = useState("solidario");

  const [fechaInicio, setFechaInicio] = useState(hoyISO());
  const [fechaFin, setFechaFin] = useState(hoyISO());
  const [filtro, setFiltro] = useState("");
  const [lote, setLote] = useState("");

  const [appliedFilters, setAppliedFilters] = useState({
    fechaInicio: hoyISO(),
    fechaFin: hoyISO(),
    filtro: "",
    lote: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  const [dataPaginada, setDataPaginada] = useState([]);
  const [numeroUltimoLote, setNumeroUltimoLote] = useState(0);
  const [loading, setLoading] = useState(false);

  // Créditos sin firma electrónica: el banco los compra directo y no aparecen en la consulta
  // paginada (esa solo trae TipoFirmaElectronica = 2). Se traen aparte, por "Cargar selección
  // Excel", y se funden en la misma tabla para operarlos con los mismos checkboxes y el mismo
  // "Generar Lote".
  const [filasExcel, setFilasExcel] = useState([]);
  // Filas que el Excel sí identificó pero que ya pertenecen a otro lote: se muestran igual (no se
  // ocultan en silencio) pero no son seleccionables - el backend las excluiría de la generación.
  const [filasEnLote, setFilasEnLote] = useState([]);
  // "normal" (seleccionar-desde-excel + Generar Lote por ids, el flujo de siempre) o "avanzado"
  // (cuota avanzada: todo-en-uno en ProyectoSolidario, se reenvía el mismo Excel al generar).
  const [modoCarga, setModoCarga] = useState("normal");
  const [archivoAvanzadoActual, setArchivoAvanzadoActual] = useState(null);
  const [menuCargaAnchor, setMenuCargaAnchor] = useState(null);
  // Override del usuario: tratar toda la selección como sin firma electrónica (no reserva oferta),
  // aunque alguna tenga TipoFirmaElectronica=2 en la BD. Solo se ofrece si la selección mezcla ambos.
  const [forzarSinFirmaElectronica, setForzarSinFirmaElectronica] = useState(false);
  const [generandoAvanzado, setGenerandoAvanzado] = useState(false);

  const data = useMemo(() => {
    const idsExcel = new Set([...filasExcel, ...filasEnLote].map((f) => f.idCre_SolicitudWeb));
    return [...filasExcel, ...filasEnLote, ...dataPaginada.filter((row) => !idsExcel.has(row.idCre_SolicitudWeb))];
  }, [filasExcel, filasEnLote, dataPaginada]);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSftpOpen, setConfirmSftpOpen] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [enviandoSftp, setEnviandoSftp] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [cargandoSeleccion, setCargandoSeleccion] = useState(false);
  const [confirmandoCompra, setConfirmandoCompra] = useState(false);
  const [archivoConfirmacion, setArchivoConfirmacion] = useState(null);
  const [confirmCompraOpen, setConfirmCompraOpen] = useState(false);
  const archivoConfirmacionRef = useRef(null);
  const archivoSeleccionRef = useRef(null);
  const archivoSeleccionAvanzadoRef = useRef(null);

  // Quitar del lote es una acción por fila (no masiva): solo tiene sentido para una
  // solicitud que YA tiene un N. Lote asignado, y se confirma/opera de a una.
  const [idParaQuitar, setIdParaQuitar] = useState(null);
  const [quitandoId, setQuitandoId] = useState(null);

  const fetchSolicitudes = useCallback(async (page, limit, filters) => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const response = await axios.get(
        APIURL.getCarteraBSSolicitudes({ limit, offset, ...filters }),
      );
      setDataPaginada(response.data?.data || []);
      setTotal(response.data?.total || 0);
      setNumeroUltimoLote(response.data?.numeroUltimoLote || 0);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo cargar el listado",
        subtitle: "Intenta nuevamente en unos segundos.",
      });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    fetchSolicitudes(currentPage, itemsPerPage, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, appliedFilters]);

  const handleConsultar = () => {
    setCurrentPage(1);
    setAppliedFilters({ fechaInicio, fechaFin, filtro, lote });
  };

  const changePage = (page) => setCurrentPage(page);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const currentPageIds = useMemo(
    () => data.filter((d) => !d.yaEnLote).map((d) => d.idCre_SolicitudWeb),
    [data],
  );
  const allCurrentSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));
  const someCurrentSelected = currentPageIds.some((id) => selectedIds.has(id)) && !allCurrentSelected;
  const selectedRows = useMemo(
    () => data.filter((row) => selectedIds.has(row.idCre_SolicitudWeb)),
    [data, selectedIds],
  );
  const hasConfirmedSelected = selectedRows.some((row) => row.confirmado === true);
  const hasSinFirmaElectronicaSelected = selectedRows.some((row) => row.sinFirmaElectronica === true);
  const seleccionMezclaFirma = hasSinFirmaElectronicaSelected
    && selectedRows.some((row) => row.sinFirmaElectronica !== true);
  // Pendientes (no confirmados) donde al menos uno ya tiene lote de un intento anterior (se
  // quitaron algunas solicitudes y se regenera el resto, o se agregan otras nuevas al mismo
  // lote): el backend reutiliza ese lote sin pedir uno nuevo. Solo las que todavía estén en
  // lote 0 (las nuevas) pasan de nuevo por reserva de oferta - las que ya tenían el lote, no.
  const pendientesSeleccionados = selectedIds.size > 0 && selectedRows.length === selectedIds.size
    && !selectedRows.some((row) => row.confirmado === true)
    ? selectedRows
    : [];
  const loteExistentePendienteSeleccionado = pendientesSeleccionados.length > 0
    ? [...new Set(pendientesSeleccionados.map((row) => Number(row.lote) || 0).filter((lote) => lote > 0))]
    : [];
  const pendienteConLoteExistente = loteExistentePendienteSeleccionado.length === 1;
  const cantidadNuevasEnLotePendiente = pendientesSeleccionados.filter((row) => (Number(row.lote) || 0) === 0).length;
  const confirmedSelectionOnly = selectedIds.size > 0
    && selectedRows.length === selectedIds.size
    && selectedRows.every((row) => row.confirmado === true);

  const toggleAllCurrentPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allCurrentSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const descargarZipCartera = async (resultado) => {
    const archivos = [resultado?.clientes, resultado?.creditos, resultado?.cuotas]
      .filter((r) => r?.ok && r?.content && r?.fileName);

    if (archivos.length === 0) return;

    try {
      const zip = new JSZip();
      archivos.forEach((archivo) => zip.file(archivo.fileName, archivo.content));

      const blob = await zip.generateAsync({ type: "blob" });
      const downloadUrl = window.URL.createObjectURL(blob);

      const now = new Date();
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const loteSuffix = resultado?.creditos?.numeroLote ? `_Lote${resultado.creditos.numeroLote}` : "";
      const zipFileName = `cartera_banco_solidario_${dd}${mm}${loteSuffix}.zip`;

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo generar el archivo .zip de descarga",
        subtitle: "Los datos ya se generaron correctamente en el banco; intenta descargarlos manualmente si es necesario.",
      });
    }
  };

  const handleGenerarCartera = async () => {
    setConfirmOpen(false);
    setGenerando(true);
    try {
      const response = await axios.post(APIURL.postGenerarCarteraBS(), {
        idsCre_SolicitudWeb: Array.from(selectedIds),
        forzarSinFirmaElectronica: forzarSinFirmaElectronica,
      });
      const resultado = response.data;
      const pasos = [
        ...(!resultado?.regeneracion && resultado?.reservas?.length > 0 ? [{
          nombre: "Reserva de ofertas",
          resultado: {
            ok: Array.isArray(resultado?.reservas) && resultado.reservas.every((reserva) => reserva.ok),
            message: resultado?.reservas
              ?.filter((reserva) => !reserva.ok)
              .map((reserva) => `${reserva.idCre_SolicitudWeb}: ${reserva.message}`)
              .join(" | "),
          },
        }] : []),
        { nombre: "Clientes", resultado: resultado?.clientes },
        { nombre: "Créditos", resultado: resultado?.creditos },
        { nombre: "Cuotas", resultado: resultado?.cuotas },
        ...(!resultado?.regeneracion ? [{ nombre: "Registro del lote", resultado: resultado?.persistencia }] : []),
      ];
      const fallidos = pasos.filter((p) => !p.resultado?.ok);

      if (fallidos.length === 0) {
        addNotification({
          type: "success",
            message: resultado?.regeneracion ? "Lote regenerado correctamente" : "Lote generado correctamente",
          subtitle: resultado?.creditos?.numeroLote
            ? `Lote N.º ${resultado.creditos.numeroLote}`
            : undefined,
        });
        // Las filas cargadas por Excel (sin firma electrónica) ya quedaron con lote asignado en
        // la BD, pero esta tabla no las puede volver a consultar (el listado paginado solo trae
        // TipoFirmaElectronica = 2): se limpian para no dejar un "lote 0" desactualizado en pantalla.
        setFilasExcel([]);
        setFilasEnLote([]);
      } else {
        const mensajesFallidos = fallidos.map((p) => ({
          nombre: p.nombre,
          mensaje: p.resultado?.message || "no se pudo generar",
        }));
        const mensajesUnicos = [
          ...new Set(mensajesFallidos.map(({ mensaje }) => mensaje)),
        ];
        const subtitle = mensajesUnicos
          .map((mensaje) => {
            const pasosConMismoMensaje = mensajesFallidos.filter(
              (item) => item.mensaje === mensaje
            );
            return pasosConMismoMensaje.length === fallidos.length
              ? mensaje
              : `${pasosConMismoMensaje.map((item) => item.nombre).join(", ")}: ${mensaje}`;
          })
          .join(" | ");

        addNotification({
          type: "error",
          message: "El lote se generó con errores",
          subtitle,
        });
      }

      fetchSolicitudes(currentPage, itemsPerPage, appliedFilters);
      await descargarZipCartera(resultado);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo generar el lote",
        subtitle: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setGenerando(false);
    }
  };

  const descargarExcelBase64 = (base64, fileName) => {
    if (!base64) return;
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    const blob = new Blob([bytes], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName || "documentos_banco_solidario.xlsx";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleEnviarDocumentosSftp = async () => {
    setConfirmSftpOpen(false);
    setEnviandoSftp(true);
    try {
      const response = await axios.post(APIURL.postEnviarDocumentosSftpBS(), {
        idsCre_SolicitudWeb: Array.from(selectedIds),
      }, { timeout: 300_000 });
      const resultado = response.data;

      descargarExcelBase64(resultado?.excelBase64, resultado?.excelFileName);

      const enviados = (resultado?.resultados || []).filter((item) => item.ok).length;
      const fallidos = (resultado?.resultados || []).filter((item) => !item.ok).length;
      const omitidos = resultado?.skipped?.length || 0;

      if (resultado?.ok) {
        addNotification({
          type: "success",
          message: "Documentos enviados al SFTP",
          subtitle: `${enviados} solicitud${enviados !== 1 ? "es" : ""} procesada${enviados !== 1 ? "s" : ""}. El Excel se descargó automáticamente.`,
        });
        setSelectedIds(new Set());
      } else {
        addNotification({
          type: "error",
          message: "El envío al SFTP terminó con novedades",
          subtitle: resultado?.message || `${enviados} enviadas, ${fallidos} con error y ${omitidos} sin lote.`,
        });
      }
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudieron enviar los documentos al SFTP",
        subtitle: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setEnviandoSftp(false);
    }
  };

  const handleQuitarFilaExcel = (id) => {
    setFilasExcel((prev) => prev.filter((f) => f.idCre_SolicitudWeb !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleQuitarDelLote = async () => {
    const id = idParaQuitar;
    setIdParaQuitar(null);
    setQuitandoId(id);
    try {
      const response = await axios.post(APIURL.postDesgenerarCarteraBS(), {
        idsCre_SolicitudWeb: [id],
      });
      const resultado = response.data;
      const skipped = resultado?.skipped || [];

      if (skipped.length === 0) {
        addNotification({
          type: "success",
          message: "Solicitud quitada del lote",
          subtitle: "Vuelve a quedar pendiente para un próximo lote de Banco Solidario.",
        });
      } else {
        addNotification({
          type: "error",
          message: "No se pudo quitar del lote",
          subtitle: skipped[0]?.reason,
        });
      }

      fetchSolicitudes(currentPage, itemsPerPage, appliedFilters);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo quitar del lote",
        subtitle: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setQuitandoId(null);
    }
  };

  const handleExportarExcel = async () => {
    setExportando(true);
    try {
      const response = await axios.get(APIURL.getCarteraBSSolicitudesExcel(appliedFilters), {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "cartera_banco_solidario.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      addNotification({ type: "success", message: "Excel generado", subtitle: "La descarga debería iniciar automáticamente." });
    } catch (error) {
      addNotification({ type: "error", message: "No se pudo generar el Excel", subtitle: "Intenta nuevamente en unos segundos." });
    } finally {
      setExportando(false);
    }
  };

  const handleSeleccionarConsolidado = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setArchivoConfirmacion(file);
    setConfirmCompraOpen(true);
  };

  const handleCargarSeleccion = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setCargandoSeleccion(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(APIURL.postSeleccionarExcelCarteraBS(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      const resultado = response.data;
      const ids = resultado?.idsCre_SolicitudWeb || [];
      const filas = resultado?.filas || [];
      const enLote = resultado?.filasEnLote || [];

      setModoCarga("normal");
      setArchivoAvanzadoActual(null);
      setFilasExcel((prev) => {
        const idsNuevos = new Set(filas.map((f) => f.idCre_SolicitudWeb));
        return [...filas, ...prev.filter((f) => !idsNuevos.has(f.idCre_SolicitudWeb))];
      });
      setFilasEnLote(enLote);
      setSelectedIds(new Set(ids));

      const omitidas = resultado?.noEncontradas?.length || 0;
      addNotification({
        type: omitidas > 0 ? "warning" : "success",
        message: `${ids.length} crédito${ids.length !== 1 ? "s" : ""} marcado${ids.length !== 1 ? "s" : ""} para vender`,
        subtitle: omitidas > 0
          ? `${omitidas} fila${omitidas !== 1 ? "s" : ""} ${omitidas !== 1 ? "no se pudieron" : "no se pudo"} seleccionar${enLote.length > 0 ? ` (${enLote.length} ya están en otro lote, se muestran abajo)` : ""}.`
          : "Revisa la selección en la tabla y luego presiona Generar lote.",
      });
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo cargar la selección de cartera",
        subtitle: error.response?.data?.message || "El Excel debe contener Numero/Factura e Identificacion.",
      });
    } finally {
      setCargandoSeleccion(false);
    }
  };

  const handleCargarSeleccionAvanzado = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setCargandoSeleccion(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(APIURL.postSeleccionarExcelAvanzadoCarteraBS(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      const resultado = response.data;
      const ids = resultado?.idsCre_SolicitudWeb || [];
      const filas = resultado?.filas || [];
      const enLote = resultado?.filasEnLote || [];

      setModoCarga("avanzado");
      // Se guarda el mismo archivo: el flujo avanzado en ProyectoSolidario es todo-en-uno (Excel
      // entra, TXT salen), así que "Generar Lote" reenvía este mismo archivo en vez de usar ids.
      setArchivoAvanzadoActual(file);
      setFilasExcel(filas);
      setFilasEnLote(enLote);
      setSelectedIds(new Set(ids));

      const omitidas = resultado?.noEncontradas?.length || 0;
      addNotification({
        type: omitidas > 0 ? "warning" : "success",
        message: `${ids.length} crédito${ids.length !== 1 ? "s" : ""} marcado${ids.length !== 1 ? "s" : ""} para cuota avanzada`,
        subtitle: omitidas > 0
          ? `${omitidas} fila${omitidas !== 1 ? "s" : ""} ${omitidas !== 1 ? "no se pudieron" : "no se pudo"} seleccionar${enLote.length > 0 ? ` (${enLote.length} ya están en otro lote, se muestran abajo)` : ""}.`
          : "Revisa la selección en la tabla y luego presiona Generar lote.",
      });
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo cargar la selección de cuota avanzada",
        subtitle: error.response?.data?.message || "El Excel debe contener Numero e Identificacion.",
      });
    } finally {
      setCargandoSeleccion(false);
    }
  };

  const handleGenerarCarteraAvanzada = async () => {
    if (!archivoAvanzadoActual) return;
    setConfirmOpen(false);
    setGenerandoAvanzado(true);
    try {
      const formData = new FormData();
      formData.append("file", archivoAvanzadoActual);
      const response = await axios.post(APIURL.postGenerarAvanzadoCarteraBS(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180_000,
        responseType: "blob",
      });

      const disposition = response.headers["content-disposition"] || "";
      const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] || "cartera_avanzado.zip";
      const downloadUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      addNotification({
        type: "success",
        message: "Lote de cuota avanzada generado",
        subtitle: "El .zip con los TXT y el SQL de persistencia se descargó automáticamente.",
      });

      setFilasExcel([]);
      setFilasEnLote([]);
      setArchivoAvanzadoActual(null);
      setModoCarga("normal");
      setSelectedIds(new Set());
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo generar el lote de cuota avanzada",
        subtitle: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    } finally {
      setGenerandoAvanzado(false);
    }
  };

  const handleConfirmarCompra = async () => {
    if (!archivoConfirmacion) return;
    setConfirmCompraOpen(false);
    setConfirmandoCompra(true);
    try {
      const formData = new FormData();
      formData.append("file", archivoConfirmacion);
      const response = await axios.post(APIURL.postConfirmarCompraCarteraBS(), formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120_000,
      });
      const resultado = response.data;
      addNotification({
        type: "success",
        message: `Compra del lote ${resultado.numeroLote} confirmada`,
        subtitle: `${resultado.confirmadas} crédito${resultado.confirmadas !== 1 ? "s" : ""} confirmado${resultado.confirmadas !== 1 ? "s" : ""} y ${resultado.descartadas} descartado${resultado.descartadas !== 1 ? "s" : ""}.`,
      });
      setSelectedIds(new Set());
      fetchSolicitudes(currentPage, itemsPerPage, appliedFilters);
    } catch (error) {
      addNotification({
        type: "error",
        message: "No se pudo confirmar la compra",
        subtitle: error.response?.data?.message || "El Excel no pudo validarse o actualizarse.",
      });
    } finally {
      setArchivoConfirmacion(null);
      setConfirmandoCompra(false);
    }
  };

  return (
    <div className="p-8 bg-white min-h-screen">
      {/* Encabezado */}
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <img
            src="/img/Solidario+.jpg"
            alt="Banco Solidario"
            className="w-12 h-12 rounded-lg object-cover shadow-sm"
          />
          <h1 className="text-2xl font-bold text-[#01BEDE]">Banco Solidario - Cartera de Créditos</h1>
        </div>
        <select
          value={banco}
          onChange={(e) => setBanco(e.target.value)}
          className="border border-gray-300 rounded-md text-sm px-3 py-2 text-gray-600 focus:ring-2 focus:ring-[#01BEDE] focus:border-[#01BEDE] outline-none"
        >
          {BANCOS.map((b) => (
            <option key={b.value} value={b.value} disabled={b.disabled}>
              {b.label}
            </option>
          ))}
        </select>
      </div>

      {/* Filtros */}
      <FiltrosCarteraBS
        fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
        fechaFin={fechaFin} setFechaFin={setFechaFin}
        filtro={filtro} setFiltro={setFiltro}
        lote={lote} setLote={setLote}
        numeroUltimoLote={numeroUltimoLote}
        handleConsultar={handleConsultar} loadingConsulta={loading}
      />

      {/* Barra de acción */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-4 text-sm font-bold text-gray-700">
          <span>
            {total} REGISTRO{total !== 1 ? "S" : ""}
            {filasExcel.length > 0 && ` + ${filasExcel.length} ${modoCarga === "avanzado" ? "CUOTA AVANZADA" : "SIN FIRMA ELECTRÓNICA"}`}
            {filasEnLote.length > 0 && ` (+${filasEnLote.length} ya en otro lote)`}
            {" - "}{selectedIds.size} SELECCIONADO{selectedIds.size !== 1 ? "S" : ""}
          </span>
          <span className="text-gray-300">|</span>
          <span>ESTADO: PENDIENTE / CONFIRMADO</span>
        </div>
        <div className="flex gap-3">
          <input
            ref={archivoSeleccionRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleCargarSeleccion}
          />
          <input
            ref={archivoSeleccionAvanzadoRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleCargarSeleccionAvanzado}
          />
          <input
            ref={archivoConfirmacionRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={handleSeleccionarConsolidado}
          />
          <button
            className="bg-[#EA580C] text-white px-6 py-2 rounded-lg shadow-lg shadow-[#EA580C]/20 font-semibold hover:bg-[#C2410C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={(e) => setMenuCargaAnchor(e.currentTarget)}
            disabled={cargandoSeleccion || generando || generandoAvanzado || enviandoSftp || confirmandoCompra}
            title="Carga el Excel previo y marca en la tabla los créditos que se enviarán al banco"
          >
            {cargandoSeleccion && <Spinner className="h-4 w-4 text-white" />}
            {cargandoSeleccion ? "Marcando..." : "Cargar selección Excel"}
          </button>
          <Menu anchorEl={menuCargaAnchor} open={!!menuCargaAnchor} onClose={() => setMenuCargaAnchor(null)}>
            <MenuItem onClick={() => { setMenuCargaAnchor(null); archivoSeleccionRef.current?.click(); }}>
              Venta completa (normal)
            </MenuItem>
            <MenuItem onClick={() => { setMenuCargaAnchor(null); archivoSeleccionAvanzadoRef.current?.click(); }}>
              Cuota avanzada
            </MenuItem>
          </Menu>
          <button
            className="bg-[#01BEDE] text-white px-6 py-2 rounded-lg shadow-lg shadow-[#01BEDE]/30 font-semibold hover:bg-[#019CB8] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => { setForzarSinFirmaElectronica(false); setConfirmOpen(true); }}
            disabled={selectedIds.size === 0 || cargandoSeleccion || generando || generandoAvanzado || enviandoSftp || confirmandoCompra}
          >
            {(generando || generandoAvanzado) && <Spinner className="h-4 w-4 text-white" />}
            {generando || generandoAvanzado ? "Generando..." : "Generar Lote"}
          </button>
          <button
            className="bg-[#0F766E] text-white px-6 py-2 rounded-lg shadow-lg shadow-[#0F766E]/30 font-semibold hover:bg-[#0B5F59] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => setConfirmSftpOpen(true)}
            disabled={selectedIds.size === 0 || hasConfirmedSelected || hasSinFirmaElectronicaSelected || enviandoSftp || generando}
            title={
              hasSinFirmaElectronicaSelected
                ? "No aplica envío de documentos SFTP para créditos sin firma electrónica"
                : hasConfirmedSelected
                  ? "Las compras confirmadas no pueden volver a enviarse al SFTP"
                  : undefined
            }
          >
            {enviandoSftp && <Spinner className="h-4 w-4 text-white" />}
            {enviandoSftp ? "Enviando..." : "Enviar documentos SFTP"}
          </button>
          <button
            className="bg-[#7C3AED] text-white px-6 py-2 rounded-lg shadow-lg shadow-[#7C3AED]/20 font-semibold hover:bg-[#6D28D9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={() => archivoConfirmacionRef.current?.click()}
            disabled={confirmandoCompra || generando || enviandoSftp}
          >
            {confirmandoCompra && <Spinner className="h-4 w-4 text-white" />}
            {confirmandoCompra ? "Confirmando..." : "Confirmar compra"}
          </button>
          <button
            className="border-2 border-[#01BEDE] text-[#01BEDE] px-6 py-2 rounded-lg font-semibold hover:bg-[#01BEDE]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleExportarExcel}
            disabled={exportando || total === 0}
          >
            {exportando && <Spinner className="h-4 w-4 text-[#01BEDE]" />}
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left">
                <SolidarioCheckbox
                  checked={allCurrentSelected}
                  indeterminate={someCurrentSelected}
                  onChange={toggleAllCurrentPage}
                />
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Lote</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">N. Solicitud</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Fecha</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Cédula</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Nombre del Cliente</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">N. Operación</th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {!loading && data.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  No hay solicitudes de Banco Solidario para mostrar.
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <CarteraTableRow
                  key={row.idCre_SolicitudWeb}
                  data={row}
                  index={index}
                  checked={selectedIds.has(row.idCre_SolicitudWeb)}
                  onToggle={toggleRow}
                  onQuitarLote={setIdParaQuitar}
                  onQuitarExcel={handleQuitarFilaExcel}
                  quitando={quitandoId === row.idCre_SolicitudWeb}
                />
              ))
            )}
            {/* Filas de relleno: refuerzan el estado de carga con un fundido, igual
                que el mockup — no son shimmer animado, son un desvanecido estático. */}
            {loading && PLACEHOLDER_ROWS.map((p, i) => (
              <tr key={`placeholder-${i}`} className={p.opacity}>
                <td className="px-4 py-4"><div className="h-4 w-4 bg-gray-200 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-8 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-20 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-48 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-24 bg-gray-100 rounded" /></td>
                <td className="px-4 py-4"><div className="h-4 w-20 bg-gray-100 rounded" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="mt-6">
          <div className="hidden md:flex justify-between items-center">
            <div className="w-1/3" />
            <div className="flex justify-center items-center gap-4 w-1/3">
              <button
                onClick={() => changePage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ‹
              </button>
              <span className="font-semibold text-gray-600">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => changePage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ›
              </button>
            </div>
            <div className="w-1/3 flex justify-end">
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {[10, 20, 30, 50].map((value) => (
                  <option key={value} value={value}>{value} por página</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex md:hidden flex-col gap-4 items-center">
            <div className="w-full flex justify-center">
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {[10, 20, 30, 50].map((value) => (
                  <option key={value} value={value}>{value} por página</option>
                ))}
              </select>
            </div>
            <div className="flex justify-center items-center gap-4">
              <button
                onClick={() => changePage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ‹
              </button>
              <span className="font-semibold text-gray-600">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => changePage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-[#01BEDE] text-white rounded-lg hover:bg-[#019CB8] transition disabled:invisible"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar generación de lote</DialogTitle>
        <DialogContent>
          {seleccionMezclaFirma && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={forzarSinFirmaElectronica}
                    onChange={(e) => setForzarSinFirmaElectronica(e.target.checked)}
                  />
                }
                label="Tratar toda la selección como sin firma electrónica (no reservar oferta). Úsalo solo si confirmas que ninguna de estas solicitudes tiene reserva en el banco."
              />
            </Alert>
          )}
          <Typography>
            {modoCarga === "avanzado" ? (
              <>Se generará el lote de <strong>cuota avanzada</strong> para <strong>{selectedIds.size}</strong> solicitud
                {selectedIds.size !== 1 ? "es" : ""} (saldo pendiente, no el crédito completo).</>
            ) : confirmedSelectionOnly ? (
              <>Se regenerarán los archivos del lote existente para <strong>{selectedIds.size}</strong> solicitud
                {selectedIds.size !== 1 ? "es" : ""} confirmada{selectedIds.size !== 1 ? "s" : ""}.</>
            ) : pendienteConLoteExistente ? (
              <>Se regenerarán los archivos del lote <strong>{loteExistentePendienteSeleccionado[0]}</strong> (todavía pendiente de confirmar) para <strong>{selectedIds.size}</strong> solicitud
                {selectedIds.size !== 1 ? "es" : ""}
                {cantidadNuevasEnLotePendiente > 0 && (
                  <> — <strong>{cantidadNuevasEnLotePendiente}</strong> de ellas son nuevas en este lote y se les reservará oferta</>
                )}.</>
            ) : (
              <>Las <strong>{selectedIds.size}</strong> solicitud{selectedIds.size !== 1 ? "es" : ""} seleccionada
                {selectedIds.size !== 1 ? "s" : ""} se enviarán como propuesta de venta a Banco Solidario.</>
            )}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {modoCarga === "avanzado"
              ? "No se reserva oferta ni se usa la compra completa: CREDITOS/CUOTAS se calculan solo sobre el saldo de capital pendiente de cada compra."
              : confirmedSelectionOnly
                ? "Solo se regenerarán Clientes, Créditos y Cuotas con el mismo número de lote. No se reservará nuevamente la oferta ni se modificará la compra."
                : pendienteConLoteExistente
                  ? cantidadNuevasEnLotePendiente > 0
                    ? "Se reutiliza el mismo número de lote sin pedir uno nuevo. Las solicitudes que ya estaban en el lote no se vuelven a reservar; solo las nuevas (si tienen firma electrónica) pasan por reserva de oferta."
                    : "Se reutiliza el mismo número de lote sin pedir uno nuevo y sin volver a reservar oferta (ya se reservó en el intento anterior). Útil después de quitar algunas solicitudes del lote."
                  : (hasSinFirmaElectronicaSelected && !seleccionMezclaFirma) || forzarSinFirmaElectronica
                    ? "Estas solicitudes no pasan por reserva de oferta (sin firma electrónica). Se generarán directamente los archivos de Clientes, Créditos y Cuotas del lote."
                    : "Al confirmar se reservará cada oferta en Banco Solidario y, únicamente si todas son aceptadas, se generarán los archivos de Clientes, Créditos y Cuotas del lote."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            onClick={modoCarga === "avanzado" ? handleGenerarCarteraAvanzada : handleGenerarCartera}
            variant="contained"
            sx={{ backgroundColor: "#01BEDE", "&:hover": { backgroundColor: "#019CB8" } }}
          >
            {modoCarga === "avanzado" ? "Generar cuota avanzada" : confirmedSelectionOnly ? "Regenerar archivos" : "Confirmar y generar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmSftpOpen} onClose={() => setConfirmSftpOpen(false)}>
        <DialogTitle>Confirmar envío de documentos</DialogTitle>
        <DialogContent>
          <Typography>
            Se enviarán al SFTP los documentos de <strong>{selectedIds.size}</strong> solicitud
            {selectedIds.size !== 1 ? "es" : ""}. Al terminar se descargará el Excel del envío.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Las solicitudes que todavía estén en lote 0 se enviarán al futuro lote N.º{" "}
            <strong>{numeroUltimoLote + 1}</strong>. Las solicitudes que ya tengan un lote asignado conservarán el suyo.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSftpOpen(false)}>Cancelar</Button>
          <Button onClick={handleEnviarDocumentosSftp} variant="contained" sx={{ backgroundColor: "#0F766E", "&:hover": { backgroundColor: "#0B5F59" } }}>
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmCompraOpen} onClose={() => setConfirmCompraOpen(false)}>
        <DialogTitle>Confirmar compra de cartera</DialogTitle>
        <DialogContent>
          <Typography>
            Se procesará el consolidado <strong>{archivoConfirmacion?.name}</strong> y se marcarán como <strong>vendidas</strong> (Confirmado = Sí) únicamente las compras incluidas por el banco en ese Excel.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Este es el <strong>Excel que manda el banco</strong> con la confirmación real de la compra (distinto del Excel de selección). Debe tener, en <strong>Hoja1</strong>, las columnas: <strong>LOTE, IDENTIFICACION, FACTURA, OPERACION, MONTOBRUTO, VALORACREDITADO, FONDOGARANTIA, SOLCA, PLAZO</strong> y <strong>DESEMBOLSO</strong>. MONTOBRUTO debe cuadrar exacto con VALORACREDITADO + FONDOGARANTIA + SOLCA.
          </Alert>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Los créditos del mismo lote que no estén en el Excel serán descartados y liberados para un lote posterior. La operación se ejecuta en una sola transacción.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmCompraOpen(false); setArchivoConfirmacion(null); }}>Cancelar</Button>
          <Button onClick={handleConfirmarCompra} variant="contained" sx={{ backgroundColor: "#7C3AED", "&:hover": { backgroundColor: "#6D28D9" } }}>
            Confirmar compra
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={idParaQuitar !== null} onClose={() => setIdParaQuitar(null)}>
        <DialogTitle>Quitar del lote de Banco Solidario</DialogTitle>
        <DialogContent>
          <Typography>
            Se quitará esta solicitud de su lote (vuelve a quedar pendiente). Solo aplica si el banco todavía no confirmó la compra.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdParaQuitar(null)}>Cancelar</Button>
          <Button onClick={handleQuitarDelLote} variant="contained" color="error">
            Quitar del Lote
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
